import { ApolloClient, ApolloLink, InMemoryCache } from '@apollo/client'
import { SetContextLink } from '@apollo/client/link/context'
import { GraphQLWsLink } from '@apollo/client/link/subscriptions'
import { getMainDefinition } from '@apollo/client/utilities'
import UploadHttpLink from 'apollo-upload-client/UploadHttpLink.mjs'
import { getApp, getApps, initializeApp } from 'firebase/app'
import { getAuth, User } from 'firebase/auth'
import { createClient } from 'graphql-ws'
import _ from 'lodash'
import { pickBy, uniqueBy } from 'remeda'
import { config as appConfig, firebaseConfig } from './common/config/constants'
import {
  CloudLoggingLogsInput,
  GetPaginatedCompaniesInput,
  GetPaginatedContractMismatchReportOperationGroupsInput,
  GetPaginatedContractsInput,
  GetPaginatedIntegrationRequestsInput,
  GetPaginatedTemplatesInput,
  GetPaginatedWriteSyncOperationsInput,
} from './common/graphql/apollo-operations'

const wsLink = new GraphQLWsLink(
  createClient({
    url: appConfig.url.SUBSCRIPTIONS_URL,
    connectionParams: async () => ({
      authToken: await getAuthToken(),
    }),
    lazy: true,
  })
)

const uploadLink = new UploadHttpLink({
  uri: appConfig.url.GRAPHQL_API_URL,
  headers: { 'Apollo-Require-Preflight': 'true' },
})

// Using the ability to split links, you can send data to each link
// depending on what kind of operation is being sent
const link = ApolloLink.split(
  // Split based on operation type
  ({ query }) => {
    const definition = getMainDefinition(query)
    return definition.kind === 'OperationDefinition' && definition.operation === 'subscription'
  },
  wsLink,
  uploadLink
)

export function getCurrentUser(): User | null {
  const auth = getAuth(getApp())
  return auth.currentUser
}

async function getAuthToken(): Promise<string | null> {
  const currentUser = getCurrentUser()
  if (!currentUser) {
    return null
  }
  return currentUser.getIdToken()
}

export const getCurrentUserAuthorization = async () => {
  const token = await getAuthToken()
  if (!token) {
    return ''
  }
  return `Bearer ${token}`
}

const authLink = new SetContextLink(async ({ headers }) => {
  // Return the headers to the context so httpLink can read them
  return {
    headers: {
      ...headers,
      authorization: await getCurrentUserAuthorization(),
    },
  }
})

// This enforces that developers think about pagination invalidation when adding new filters.
// For each field, specify whether it should invalidate the paginated list.
// Typically this is true for all filters and sort keys, false for everything related to pagination (limit, cursor).
const paginatedTemplateInvalidation: {
  [key in keyof Required<GetPaginatedTemplatesInput>]: boolean
} = {
  cursor: false,
  limit: false,
  sortCriteria: true,
  sortOrder: true,
  search: true,
  status: true,
  assigneeId: true,
  waitingOnId: true,
  relatedCompanyId: true,
  associatedContractId: true,
  tags: true,
  type: true,
  includeInactive: true,
  isUnused: true,
}
const paginatedTemplateInvalidationKeys = _.keys(
  pickBy(paginatedTemplateInvalidation, (include) => include)
)

const paginatedContractInvalidation: {
  [key in keyof Required<GetPaginatedContractsInput>]: boolean
} = {
  cursor: false,
  limit: false,
  month: true,
  billingType: true,
  contractStatus: true,
  leadPMIds: true,
  generalContractorIds: true,
  officeIds: true,
  projectIds: true,
  templateIds: true,
  payAppStatus: true,
  complianceStatus: true,
  hasBillingForecast: true,
  search: true,
  sortCriteria: true,
  sortOrder: true,
  submitVia: true,
  isProcessingForms: true,
}
const paginatedContractInvalidationKeys = _.keys(
  pickBy(paginatedContractInvalidation, (include) => include)
)

const paginatedCompanyInvalidation: {
  [key in keyof Required<GetPaginatedCompaniesInput>]: boolean
} = {
  cursor: false,
  limit: false,
  sortCriteria: true,
  sortOrder: true,
  search: true,
  hasChurned: true,
  type: true,
}
const paginatedCompanyInvalidationKeys = _.keys(
  pickBy(paginatedCompanyInvalidation, (include) => include)
)

const paginatedOperationsInvalidation: {
  [key in keyof Required<GetPaginatedWriteSyncOperationsInput>]: boolean
} = {
  cursor: false,
  limit: false,
  search: true,
  status: true,
  sort: true,
  includeFileBasedExports: true,
  includeManualSyncOperations: true,
}
const paginatedOperationsInvalidationKeys = _.keys(
  pickBy(paginatedOperationsInvalidation, (include) => include)
)

const paginatedIntegrationRequestsInvalidation: {
  [key in keyof Required<GetPaginatedIntegrationRequestsInput>]: boolean
} = {
  cursor: false,
  limit: false,
  search: true,
  integrationType: true,
  startDate: true,
  endDate: true,
}
const paginatedIntegrationRequestsInvalidationKeys = _.keys(
  pickBy(paginatedIntegrationRequestsInvalidation, (include) => include)
)

const paginatedContractMismatchReportOperationGroupsInvalidation: {
  [key in keyof Required<GetPaginatedContractMismatchReportOperationGroupsInput>]: boolean
} = {
  cursor: false,
  limit: false,
  companyId: true,
  endDate: true,
  search: true,
  startDate: true,
}
const paginatedContractMismatchReportOperationGroupsInvalidationKeys = _.keys(
  pickBy(paginatedContractMismatchReportOperationGroupsInvalidation, (include) => include)
)

const paginatedLogsInvalidation: {
  [key in keyof Required<CloudLoggingLogsInput>]: boolean
} = {
  pageToken: false,
  pageSize: true,
  filter: true,
  orderBy: true,
  autoPaginate: true,
}
const paginatedLogsInvalidationKeys = _.keys(
  pickBy(paginatedLogsInvalidation, (include) => include)
)

export const apolloClient = new ApolloClient({
  link: ApolloLink.from([authLink, link]),
  // In Apollo Client v4, notifyOnNetworkStatusChange defaults to true. This causes
  // useQuery to emit intermediate loading states during fetchMore, which triggers
  // react-infinite-scroller to re-render and re-call loadMore before the merged data
  // is delivered. The overlapping fetchMore calls block Apollo's internal notification
  // mechanism for non-cache-and-network policies, creating an infinite request loop.
  defaultOptions: {
    watchQuery: {
      notifyOnNetworkStatusChange: false,
    },
  },

  cache: new InMemoryCache({
    typePolicies: {
      Query: {
        fields: {
          // See https://www.apollographql.com/docs/react/pagination/cursor-based/#using-list-element-ids-as-cursors
          paginatedTemplates: {
            keyArgs: [
              'input',
              // If any of the following filters/sort changes, apollo will create a new paginated list instead of merging.
              paginatedTemplateInvalidationKeys,
            ],
            merge(existing, incoming, { readField }) {
              const existingTemplates = existing?.templates ?? []
              const mergedTemplates = uniqueBy(
                [...existingTemplates, ...incoming.templates],
                (template) => readField('id', template)
              )
              return { ...incoming, templates: mergedTemplates }
            },
          },

          // See https://www.apollographql.com/docs/react/pagination/cursor-based/#using-list-element-ids-as-cursors
          paginatedContracts: {
            keyArgs: [
              'input',
              // If any of the following filters/sort changes, apollo will create a new paginated list instead of merging.
              paginatedContractInvalidationKeys,
            ],
            merge(existing, incoming, { readField }) {
              const existingContracts = existing?.contracts ?? []
              const mergedContracts = uniqueBy(
                [...existingContracts, ...incoming.contracts],
                (contract) => readField('id', contract)
              )
              return { ...incoming, contracts: mergedContracts }
            },
          },

          // See https://www.apollographql.com/docs/react/pagination/cursor-based/#using-list-element-ids-as-cursors
          paginatedCompanies: {
            keyArgs: [
              'input',
              // If any of the following filters/sort changes, apollo will create a new paginated list instead of merging.
              paginatedCompanyInvalidationKeys,
            ],
            merge(existing, incoming, { readField }) {
              const existingCompanies = existing?.companies ?? []
              const mergedCompanies = uniqueBy(
                [...existingCompanies, ...incoming.companies],
                (company) => readField('id', company)
              )
              return { ...incoming, companies: mergedCompanies }
            },
          },

          // See https://www.apollographql.com/docs/react/pagination/cursor-based/#using-list-element-ids-as-cursors
          paginatedWriteSyncOperations: {
            keyArgs: [
              'input',
              // If any of the following filters/sort changes, apollo will create a new paginated list instead of merging.
              paginatedOperationsInvalidationKeys,
            ],
            merge(existing, incoming, { readField }) {
              const existingOperations = existing?.operations ?? []
              const mergedOperations = uniqueBy(
                [...existingOperations, ...incoming.operations],
                (operation) => readField('id', operation)
              )
              return { ...incoming, operations: mergedOperations }
            },
          },

          // See https://www.apollographql.com/docs/react/pagination/cursor-based/#using-list-element-ids-as-cursors
          paginatedIntegrationRequests: {
            keyArgs: [
              'input',
              // If any of the following filters/sort changes, apollo will create a new paginated list instead of merging.
              paginatedIntegrationRequestsInvalidationKeys,
            ],
            merge(existing, incoming, { readField }) {
              const existingRequests = existing?.integrationRequests ?? []
              const mergedRequests = uniqueBy(
                [...existingRequests, ...incoming.integrationRequests],
                (request) => readField('id', request)
              )
              return { ...incoming, integrationRequests: mergedRequests }
            },
          },

          // See https://www.apollographql.com/docs/react/pagination/cursor-based/#using-list-element-ids-as-cursors
          paginatedContractMismatchReportOperationGroups: {
            keyArgs: ['input', paginatedContractMismatchReportOperationGroupsInvalidationKeys],
            merge(existing, incoming, { readField }) {
              const existingGroups = existing?.groups ?? []
              const mergedGroups = uniqueBy([...existingGroups, ...incoming.groups], (group) =>
                readField('id', group)
              )
              return { ...incoming, groups: mergedGroups }
            },
          },

          // See https://www.apollographql.com/docs/react/pagination/cursor-based/#using-list-element-ids-as-cursors
          cloudLoggingLogs: {
            keyArgs: [
              'input',
              // If any of the following filters/sort changes, apollo will create a new paginated list instead of merging.
              paginatedLogsInvalidationKeys,
            ],
            merge(existing, incoming) {
              const existingEntries = existing?.entries ?? []
              const mergedEntries = [...existingEntries, ...incoming.entries]
              return { ...incoming, entries: mergedEntries }
            },
          },
        },
      },
    },
  }),
})

/**
 * Initializes Firebase and Firebase Auth. Sets up a watcher when the user state changes.
 * @param initialized A function to call when Firebase has been initialized
 * @param userCallback A function to call when a user is logged in and Apollo is configured
 */
export function initializeFirebaseAuth() {
  const apps = getApps()
  if (apps.length > 0) {
    return
  }

  // Initialize Firebase
  initializeApp(firebaseConfig)

  // Watch when the user changes and reset the GraphQL client store
  getAuth(getApp()).onAuthStateChanged((user) => {
    if (!user) {
      // User is logged out, clear the cache
      apolloClient.clearStore()
    }
  })
}
